import {
  type DataSecurityPolicy,
  type Policies,
  type PoliciesBody,
} from '@canvas/frame-api-types';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import {
  captureMessage,
  CustomerImpact,
  EventClassification,
} from '@canvas/sentry-react';

type ValidationResult<T> =
  | {
      valid: true;
      body: T;
    }
  | { valid: false; error: string };

const validatePoliciesBody = (body: any | undefined): ValidationResult<Policies> => {
  if (!body || typeof body !== 'object') {
    return { valid: false, error: 'policies is not an object' };
  }

  if (!body.dataSecurityPolicy || typeof body.dataSecurityPolicy !== 'object') {
    return { valid: false, error: 'policies.dataSecurityPolicy is not an object' };
  }

  const policy = body.dataSecurityPolicy;

  if (policy.pageExport === undefined || typeof policy.pageExport !== 'boolean') {
    return { valid: false, error: 'policy.pageExport is not a boolean' };
  }
  return {
    valid: true,
    body: body,
  };
};

export const validatePoliciesAndTransformToDataSecurityPolicy = (
  policies: PoliciesBody | undefined
): DataSecurityPolicy | undefined => {
  const result = validatePoliciesBody(policies);
  if (!result.valid) {
    const errorMessage =
      'Policies response is missing data that was expected to be present by the policies schema';

    ReliabilityMetrics.getInstance().failure(MetricName.POLICIES_SCHEMA_VALIDATION, {
      errorMessage,
    });

    captureMessage(errorMessage, {
      contexts: {
        'Policies response': policies,
        'Policies schema validation error': {
          error: result.error,
        },
      },
      tags: {
        captureLocation:
          'validatePoliciesAndTransformToDataSecurityPolicy-invalid-schema',
        customerImpact: CustomerImpact.CUSTOMERS,
        eventClassification: EventClassification.BUG,
      },
      level: 'error',
    });
  } else {
    return result.body.dataSecurityPolicy;
  }
};
